import React, { useState, useEffect } from "react";
import {
  Bell,
  Lock,
  Eye,
  EyeOff,
  Shield,
  Mail,
  MessageSquare,
  Smartphone,
  Save,
} from "lucide-react";
import Hero from '../../components/ui/Hero'; // adjust path if needed
import { apiPost } from "../../api/client.js";
import {
  fetchBorrowerTwoFaStatus,
  beginBorrowerTwoFaSetup,
  confirmBorrowerTwoFaSetup,
  disableBorrowerTwoFa,
} from "../../api/borrowerPortal/borrowerTwoFa.js";

const LS_KEY = "borrower_settings";

export default function BorrowerSettings() {
  const [savedMsg, setSavedMsg] = useState("");
  const [notifications, setNotifications] = useState({
    email: true,
    sms: true,
    browser: true, // web-only (“push” -> “browser”)
    applicationStatus: true,
    documentRequests: true,
    rateChanges: true,
    appointmentReminders: true,
    closingUpdates: true,
  });

  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [twoFaEmailEnabled, setTwoFaEmailEnabled] = useState(false);
  const [twoFaEmail, setTwoFaEmail] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaVerified, setTwoFaVerified] = useState(false);
  const [setupTmpId, setSetupTmpId] = useState("");
  const [setupMethod, setSetupMethod] = useState("email");
  /** @type {"enable" | "disable" | null} */
  const [twoFaPendingAction, setTwoFaPendingAction] = useState(null);
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const [twoFaMsg, setTwoFaMsg] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.notifications) setNotifications((p) => ({ ...p, ...parsed.notifications }));
      }
    } catch {}

    (async () => {
      try {
        const status = await fetchBorrowerTwoFaStatus();
        if (!status?.ok) return;
        setTwoFaEmailEnabled(status.enabled);
        if (status.user) setTwoFaEmail(status.user);
        setTwoFaVerified(status.enabled);
      } catch {
        /* status unavailable while logged out or API error */
      }
    })();
  }, []);

  const toggleNotification = (key) =>
    setNotifications((n) => ({ ...n, [key]: !n[key] }));
  const toggleShow = (k) => setShow((v) => ({ ...v, [k]: !v[k] }));
  const onPwdChange = (e) => setPwd((p) => ({ ...p, [e.target.name]: e.target.value }));

  const saveAll = (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ notifications, twoFaEmailEnabled, twoFaEmail, twoFaVerified })
      );
      setSavedMsg("Settings saved.");
      setTimeout(() => setSavedMsg(""), 1500);
    } catch {}
    setTimeout(() => setSaving(false), 400);
  };

  const updatePassword = async () => {
    if (!pwd.current || !pwd.next || !pwd.confirm) {
      alert("Please fill in all password fields.");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      alert("New password and confirmation do not match.");
      return;
    }
    try {
      await apiPost("/api/method/frappe.core.doctype.user.user.update_password", {
        old_password: pwd.current,
        new_password: pwd.next,
        logout_all_sessions: 1,
      });
      setPwd({ current: "", next: "", confirm: "" });
      alert("Your password has been updated.");
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?._server_messages ||
        error?.message ||
        "Failed to update password. Please try again.";
      alert(msg);
    }
  };

  const sendTwoFaCode = async (action = twoFaPendingAction) => {
    const isDisable = action === "disable";
    setTwoFaBusy(true);
    setTwoFaMsg("");
    if (!isDisable) setTwoFaVerified(false);
    try {
      const res = await beginBorrowerTwoFaSetup("email");
      if (!res.tmpId) {
        throw new Error("Unable to start 2FA setup. Missing temporary ID from server.");
      }
      setSetupTmpId(res.tmpId);
      setSetupMethod(res.method || "email");
      if (res.user) setTwoFaEmail(res.user);
      const email = res.user || twoFaEmail || "your email";
      setTwoFaMsg(
        isDisable
          ? `We sent a confirmation code to ${email}. Enter it below to turn off email 2FA.`
          : res.message || `Verification code sent to ${email}. Enter it below to enable email 2FA.`
      );
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to send code.";
      alert(typeof msg === "string" ? msg : "Failed to send code.");
    } finally {
      setTwoFaBusy(false);
    }
  };

  const verifyTwoFaCode = async () => {
    if (!setupTmpId) {
      alert("Please send a verification code first.");
      return;
    }
    if (!twoFaCode.trim()) {
      alert("Please enter the verification code.");
      return;
    }
    setTwoFaBusy(true);
    setTwoFaMsg("");
    try {
      const isDisable = twoFaPendingAction === "disable";
      const res = isDisable
        ? await disableBorrowerTwoFa({
            tmp_id: setupTmpId,
            otp: twoFaCode,
            method: setupMethod,
          })
        : await confirmBorrowerTwoFaSetup({
            tmp_id: setupTmpId,
            otp: twoFaCode,
            method: setupMethod,
          });

      if (isDisable) {
        if (res.ok !== false) {
          setTwoFaEmailEnabled(false);
          setTwoFaVerified(false);
          setTwoFaMsg("Email two-factor authentication is now turned off.");
          setSetupTmpId("");
          setTwoFaCode("");
          setTwoFaPendingAction(null);
        }
      } else if (res.enabled) {
        setTwoFaEmailEnabled(true);
        setTwoFaVerified(true);
        setTwoFaMsg("Email two-factor authentication is now enabled.");
        setSetupTmpId("");
        setTwoFaCode("");
        setTwoFaPendingAction(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Invalid code.";
      alert(typeof msg === "string" ? msg : "Invalid code.");
    } finally {
      setTwoFaBusy(false);
    }
  };

  const onTwoFaToggle = async () => {
    if (twoFaBusy) return;

    if (twoFaEmailEnabled && twoFaVerified) {
      setTwoFaPendingAction("disable");
      setTwoFaCode("");
      setTwoFaMsg("");
      await sendTwoFaCode("disable");
      return;
    }

    if (twoFaEmailEnabled) return;

    setTwoFaPendingAction("enable");
    setTwoFaEmailEnabled(true);
    setTwoFaVerified(false);
    setTwoFaCode("");
    await sendTwoFaCode("enable");
  };

  const isDisableFlow = twoFaPendingAction === "disable";
  const is2faActive = twoFaEmailEnabled && twoFaVerified && !setupTmpId;

  return (
    <div className="min-h-dvh bg-white">
      <Hero className="-mx-4 sm:-mx-6" title="Settings" subtitle="Manage your preferences and security" />

      <form onSubmit={saveAll} className="p-4 sm:p-6 space-y-6">
        {/* Notifications */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
          <h3 className="mb-2 flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Bell className="text-sky-500" size={24} />
            Notifications
          </h3>
          <p className="mb-6 text-slate-600">
            Choose how you want to receive updates about your mortgage application.
          </p>

          <div className="space-y-6">
            {/* Channels */}
            <div className="border-b border-slate-200 pb-6">
              <h4 className="mb-4 text-lg font-semibold text-slate-900">
                Notification Channels
              </h4>

              <div className="grid gap-4 md:grid-cols-2">
                <ChannelRow
                  icon={Mail}
                  title="Email Notifications"
                  desc="Receive updates via email"
                  enabled={notifications.email}
                  onToggle={() => toggleNotification("email")}
                />
                <ChannelRow
                  icon={MessageSquare}
                  title="SMS Notifications"
                  desc="Get text message alerts"
                  enabled={notifications.sms}
                  onToggle={() => toggleNotification("sms")}
                />
                <ChannelRow
                  className="md:col-span-2"
                  icon={Smartphone}
                  title="Browser Notifications"
                  desc="Desktop notifications in your web browser"
                  enabled={notifications.browser}
                  onToggle={() => toggleNotification("browser")}
                />
              </div>
            </div>

            {/* Types */}
            <div>
              <h4 className="mb-4 text-lg font-semibold text-slate-900">
                What to be notified about
              </h4>

              <div className="grid gap-3 md:grid-cols-2">
                <RowToggle
                  title="Application Status Updates"
                  desc="Changes to your mortgage application status"
                  enabled={notifications.applicationStatus}
                  onToggle={() => toggleNotification("applicationStatus")}
                />
                <RowToggle
                  title="Document Requests"
                  desc="When additional documents are needed"
                  enabled={notifications.documentRequests}
                  onToggle={() => toggleNotification("documentRequests")}
                />
                <RowToggle
                  title="Interest Rate Changes"
                  desc="Updates on locked or new rates"
                  enabled={notifications.rateChanges}
                  onToggle={() => toggleNotification("rateChanges")}
                />
                <RowToggle
                  title="Appointment Reminders"
                  desc="Reminders for scheduled calls or meetings"
                  enabled={notifications.appointmentReminders}
                  onToggle={() => toggleNotification("appointmentReminders")}
                />
                <RowToggle
                  title="Closing Updates"
                  desc="Information about your closing date"
                  enabled={notifications.closingUpdates}
                  onToggle={() => toggleNotification("closingUpdates")}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Security */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
          <h3 className="mb-2 flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Shield className="text-sky-500" size={24} />
            Security
          </h3>
          <p className="mb-6 text-slate-600">Keep your account safe and secure.</p>

          <div className="space-y-6">
            {/* Password */}
            <div className="border-b border-slate-200 pb-6">
              <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <Lock size={20} className="text-sky-600" />
                Change Password
              </h4>

              <div className="grid gap-4 md:grid-cols-3">
                <PasswordInput
                  label="Current Password"
                  name="current"
                  value={pwd.current}
                  onChange={onPwdChange}
                  shown={show.current}
                  onToggle={() => toggleShow("current")}
                  placeholder="Enter current password"
                />
                <PasswordInput
                  label="New Password"
                  name="next"
                  value={pwd.next}
                  onChange={onPwdChange}
                  shown={show.next}
                  onToggle={() => toggleShow("next")}
                  placeholder="Enter new password"
                />
                <PasswordInput
                  label="Confirm New Password"
                  name="confirm"
                  value={pwd.confirm}
                  onChange={onPwdChange}
                  shown={show.confirm}
                  onToggle={() => toggleShow("confirm")}
                  placeholder="Confirm new password"
                />
              </div>

              <button
                type="button"
                onClick={updatePassword}
                className="mt-4 rounded-lg bg-sky-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all hover:bg-sky-600 hover:shadow-lg"
              >
                Update Password
              </button>
            </div>

            <div>
              <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <Mail size={20} className="text-sky-600" />
                Email Two-Factor Authentication (2FA)
              </h4>
              <p className="mb-4 text-sm text-slate-600">
                {is2faActive
                  ? "Sign-in requires a one-time code sent to your email."
                  : isDisableFlow
                    ? "Turn off 2FA by confirming with a one-time code we send to your email."
                    : "Add a verification step to sign-in using a one-time code sent to your email."}
              </p>

              <div
                className={`mb-4 flex items-center justify-between rounded-lg p-4 ${
                  isDisableFlow ? "bg-amber-50 ring-1 ring-amber-200" : "bg-slate-50"
                }`}
              >
                <div>
                  <p className="font-semibold text-slate-900">
                    {is2faActive
                      ? "Email 2FA is on"
                      : isDisableFlow
                        ? "Disable email 2FA"
                        : "Enable email 2FA"}
                  </p>
                  <p className="text-sm text-slate-600">
                    {is2faActive
                      ? "Turn off the switch to start disable — we will email you a confirmation code."
                      : isDisableFlow
                        ? "Enter the code below to confirm. Until then, 2FA stays on for sign-in."
                        : "You will be asked for an email code after entering your password."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onTwoFaToggle}
                  disabled={twoFaBusy}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-60 ${
                    twoFaEmailEnabled ? "bg-sky-500" : "bg-gray-300"
                  }`}
                  aria-label="Toggle email two-factor authentication"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-md ${
                      twoFaEmailEnabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {twoFaMsg && (
                <p
                  className={`mb-3 text-sm font-medium ${
                    isDisableFlow ? "text-amber-800" : "text-sky-700"
                  }`}
                >
                  {twoFaMsg}
                </p>
              )}

              {is2faActive ? (
                <p className="text-sm font-semibold text-emerald-700">
                  Email 2FA is active for {twoFaEmail || "your account"}.
                </p>
              ) : (
                (twoFaEmailEnabled || setupTmpId) && (
                  <div className="grid gap-3 md:grid-cols-[1fr_auto_auto]">
                    <p className="md:col-span-3 text-sm text-slate-600">
                      {twoFaPendingAction === "disable"
                        ? `Enter the code sent to ${twoFaEmail || "your email"} to disable 2FA.`
                        : `Codes are sent to ${twoFaEmail || "your account email"}.`}
                    </p>
                    <button
                      type="button"
                      onClick={() => sendTwoFaCode(isDisableFlow ? "disable" : "enable")}
                      disabled={twoFaBusy}
                      className="md:col-span-1 rounded-lg bg-slate-900 px-4 py-2.5 font-semibold text-white hover:bg-black disabled:opacity-60"
                    >
                      {twoFaBusy ? "Sending..." : "Resend code"}
                    </button>
                    <div className="flex items-center text-sm font-semibold text-emerald-700">
                      {twoFaPendingAction === "disable"
                        ? "Confirm disable"
                        : twoFaVerified
                          ? "Enabled"
                          : "Pending verification"}
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={twoFaCode}
                      onChange={(e) => setTwoFaCode(e.target.value.replace(/\s/g, ""))}
                      placeholder="Enter code"
                      className="rounded-lg border border-slate-200 px-3 py-2.5 outline-none focus:ring-2 focus:ring-sky-300"
                    />
                    <button
                      type="button"
                      onClick={verifyTwoFaCode}
                      disabled={twoFaBusy}
                      className="rounded-lg bg-sky-500 px-4 py-2.5 font-semibold text-white hover:bg-sky-600 disabled:opacity-60"
                    >
                      {twoFaBusy
                        ? "Verifying..."
                        : twoFaPendingAction === "disable"
                          ? "Confirm disable"
                          : "Verify code"}
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        </section>

        <div className="flex items-center justify-end gap-3">
          {savedMsg && <div className="text-sm font-semibold text-emerald-700">{savedMsg}</div>}
          <button
            type="submit"
            disabled={saving}
            className={
              "inline-flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-white " +
              (saving ? "bg-sky-500/70 cursor-not-allowed" : "bg-sky-500 hover:bg-sky-600")
            }
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------ Reusable bits ------------------------ */

function ChannelRow({ icon: Icon, title, desc, enabled, onToggle, className = "" }) {
  return (
    <div className={`flex items-center justify-between rounded-lg bg-sky-50 p-4 ${className}`}>
      <div className="flex items-center gap-3">
        <Icon className="text-sky-600" size={22} />
        <div>
          <p className="font-semibold text-slate-900">{title}</p>
          <p className="text-sm text-slate-600">{desc}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
          enabled ? "bg-sky-500" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-md ${
            enabled ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

function RowToggle({ title, desc, enabled, onToggle }) {
  return (
    <div className="flex items-center justify-between rounded-lg p-4 transition-colors hover:bg-slate-50">
      <div>
        <p className="font-semibold text-slate-900">{title}</p>
        <p className="text-sm text-slate-600">{desc}</p>
      </div>
      <div className="ml-4">
        <button
          type="button"
          onClick={onToggle}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
            enabled ? "bg-sky-500" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-md ${
              enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function PasswordInput({ label, name, value, onChange, shown, onToggle, placeholder }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="relative mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-sky-300">
        <input
          className="w-full bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
          type={shown ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={onToggle}
          className="p-1 rounded-lg hover:bg-slate-50 text-slate-600"
          aria-label={shown ? "Hide password" : "Show password"}
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </label>
  );
}
